import CheckCircleOutlineOutlinedIcon from '@material-ui/icons/CheckCircleOutlineOutlined';
import React, { useContext, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import { notify } from 'components/Notifier';
import { AppContext } from 'context';
import { SortingCenterContractEditFormType } from 'types';
import SortingCenterContractEditForm from 'components/Forms/SortingCenterContractEditForm';
import {
  useCharacterizationsWithFilter,
  useEstimatedReceptionBySortingCenterContract,
  useReceptionsWithFilter,
  useSortingCenterContractsById,
} from 'hooks';
import PageContent from 'components/Generic/PageContent';
import RequiredFieldsLegend from 'components/Forms/RequiredFieldsLegend';
import BottomButtonBar from 'components/Generic/BottomButtonBar';
import Loading from 'components/Loading';
import { formatEstimatedReceptionBeforePut, formatSortingCenterContractBeforePut } from 'utils/normalizer';
import { putSortingCenterContractAndEstimatedReception } from 'services/apiOperations';
import { Routes } from 'routes';
import { getLatestDate } from 'utils/date';
import { transformObjectId } from 'services/apiPlatformTransformer';
import { TAB_PANEL_PARAM } from './ClientShow';

const SortingCenterContractEdit: React.FC = () => {
  const formName = 'sortingCenterContractEditForm';

  const context = useContext(AppContext);
  const history = useHistory();
  const { t } = useTranslation();
  const { t: pt } = useTranslation('translation', { keyPrefix: 'sortingCenterContracts.update' });

  const { contractId, clientId, streamId } = useParams<{ contractId: string; clientId: string; streamId: string }>();
  const { value: sortingCenterContract } = useSortingCenterContractsById(contractId);
  const { value: estimatedReception } = useEstimatedReceptionBySortingCenterContract(
    transformObjectId(sortingCenterContract),
  );

  const [receptionList, totalReceptionFound] = useReceptionsWithFilter(
    null,
    { order: 'desc' },
    { client: clientId, stream: streamId },
  );

  const [characterizationList, totalCharacterizationFound] = useCharacterizationsWithFilter(
    null,
    { order: 'desc' },
    { client: clientId, stream: streamId },
  );

  const canEditStartDateOrWeight = totalReceptionFound === 0 && totalCharacterizationFound === 0;
  const lastReceptionDate: Date | null =
    receptionList && totalReceptionFound > 0 ? new Date(receptionList[0].receptionDatetime) : null;

  const lastCharacterizationDate: Date | null =
    characterizationList && totalCharacterizationFound > 0 ? new Date(characterizationList[0].samplingDate) : null;

  const minDateContractEdit: Date | null = getLatestDate(lastReceptionDate, lastCharacterizationDate);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const backRoute =
    generatePath(Routes.ClientShow, { clientId: String(clientId) }) + '?' + TAB_PANEL_PARAM + '=contracts';

  const onSubmit: SubmitHandler<SortingCenterContractEditFormType> = (
    sortingCenterContract: SortingCenterContractEditFormType,
  ) => {
    const sortingCenterId = context.getSortingCenter?.()?.['@id'] ?? null;
    if (null === sortingCenterId) {
      notify({
        message: t('sortingCenterContracts.update.error'),
        severity: 'error',
      });
      return;
    }
    setIsSubmitting(true);

    const formattedSortingCenterContract = formatSortingCenterContractBeforePut(sortingCenterContract, sortingCenterId);
    const formattedEstimatedReception = canEditStartDateOrWeight
      ? formatEstimatedReceptionBeforePut(sortingCenterContract, sortingCenterId)
      : undefined;

    return putSortingCenterContractAndEstimatedReception(formattedSortingCenterContract, formattedEstimatedReception)
      .then(() => {
        notify({
          message: t('sortingCenterContracts.update.success'),
          severity: 'success',
        });
        history.push(backRoute);
        setIsSubmitting(false);
      })
      .catch(() => {
        notify({
          message: t('errorGeneric'),
          severity: 'error',
        });
        setIsSubmitting(false);
      });
  };

  if (!sortingCenterContract || !estimatedReception) {
    return <Loading />;
  }

  return (
    <>
      <PageContent title={pt('editPage')} backRoute={backRoute} withButtonBar>
        <SortingCenterContractEditForm
          onSubmit={onSubmit}
          formName={formName}
          sortingCenterContract={sortingCenterContract}
          estimatedReception={estimatedReception}
          canEditStartDateOrWeight={canEditStartDateOrWeight}
          minDateContractEdit={minDateContractEdit}
        />
        <RequiredFieldsLegend />
      </PageContent>
      <BottomButtonBar
        previousButtonLink={backRoute}
        formName={formName}
        previousStepLabel={pt('previousStepButtonLabel')}
        nextStepLabel={pt('editButtonLabel')}
        nextStepIcon={<CheckCircleOutlineOutlinedIcon />}
        disableNext={isSubmitting}
      />
    </>
  );
};
export default SortingCenterContractEdit;
